import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, User, FileText, Mail, Home, Palette, Settings, BarChart3, Layout } from 'lucide-react';
import PortfolioEditor from '../components/admin/PortfolioEditor';
import ContactInbox from '../components/admin/ContactInbox';
import ThemeEditor from '../components/admin/ThemeEditor';
import LayoutEditor from '../components/admin/LayoutEditor';
import EmailSettings from '../components/admin/EmailSettings';
import PasswordChange from '../components/admin/PasswordChange';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import TypographySettings from '../components/admin/TypographySettings';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('portfolio');

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="glass border-b border-orange-500/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <User size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-gray-400">Welcome, {user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
              >
                <Home size={18} className="mr-2" />
                View Site
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="glass border border-orange-500/20 p-1">
            <TabsTrigger 
              value="portfolio" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <FileText size={18} className="mr-2" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger 
              value="contacts" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <Mail size={18} className="mr-2" />
              Contacts
            </TabsTrigger>
            <TabsTrigger 
              value="themes" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <Palette size={18} className="mr-2" />
              Themes
            </TabsTrigger>
            <TabsTrigger 
              value="layout" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <Layout size={18} className="mr-2" />
              Layout
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <Settings size={18} className="mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="analytics" 
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white"
            >
              <BarChart3 size={18} className="mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          <TabsContent value="portfolio" className="space-y-6">
            <PortfolioEditor />
          </TabsContent>

          <TabsContent value="contacts" className="space-y-6">
            <ContactInbox />
          </TabsContent>

          <TabsContent value="themes" className="space-y-6">
            <ThemeEditor />
          </TabsContent>

          <TabsContent value="layout" className="space-y-6">
            <LayoutEditor />
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <TypographySettings />
            <EmailSettings />
            <div className="border-t border-orange-500/10 pt-6">
              <PasswordChange />
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
